import { useWhatsAppGAFF } from 'modules/campaignAnalytics/hooks';
import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { BasicSelect } from 'ui/selects';
import { body2Bold, spacing } from 'ui/theme';
import { exportReportDataAsCsvRequest } from '../actions';
import { ReportValues } from '../constants';

const HiddenCSVLink = styled(CSVLink)`
  display: none;
`;

const ModalContent = styled.div`
  ${body2Bold}
  margin-bottom: ${spacing.half}
`;

type Props = {
  isExportModalOpen: boolean;
  setIsExportModalOpen: Dispatch<SetStateAction<boolean>>;
  selectedMediums: string[];
  selectedIds: number[];
  showAppPackage: boolean;
};

export const ExportEventModal: FC<React.PropsWithChildren<Props>> = ({
  isExportModalOpen,
  setIsExportModalOpen,
  selectedMediums = [],
  selectedIds,
  showAppPackage = false,
}) => {
  const isWhatsAppGAEnabled = useWhatsAppGAFF();
  const defaultMedium = selectedMediums.length ? selectedMediums[0] : 'Email';
  const defaultEvent = ReportValues(isWhatsAppGAEnabled)[defaultMedium][0].value;
  const csvLink = useRef<CSVLink & { link: HTMLAnchorElement }>(null);
  const [csvEvent, setCsvEvent] = useState(defaultEvent);
  const dispatch = useDispatch();

  const [csvData, setCsvData] = useState('');

  const clickExportCsvButtonCallback = (data: string) => {
    flushSync(() => {
      setCsvData(data);
    });

    // Callback to mimick the button click for CSVLink
    // This callback is called after the csv data is fetched, so that the data prop in the CSVLink is updated
    csvLink?.current?.link?.click();
    setIsExportModalOpen(false);
  };

  const valSet = new Set();

  const valuesByChannel = selectedMediums
    .map((medium) =>
      ReportValues(isWhatsAppGAEnabled)[medium].filter((el) => {
        if (!valSet.has(el.label)) {
          valSet.add(el.label);
          return true;
        }
        return false;
      }),
    )
    .flat();

  return (
    <Modal
      id="exportModal"
      isModalOpen={isExportModalOpen}
      onClose={() => setIsExportModalOpen(false)}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>Export events</ModalHeader>
      <ModalBody>
        <ModalContent>Select an event</ModalContent>
        <BasicSelect
          onSelect={(val) => {
            setCsvEvent(val[0]);
          }}
          defaultValues={[csvEvent]}
          id="export-events"
          options={valuesByChannel}
          title="export-events"
          isFullWidth={true}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
          isDisabled={!csvEvent}
          onClick={() => {
            dispatch(
              exportReportDataAsCsvRequest(
                {
                  campaignIds: JSON.stringify(selectedIds),
                  eventType: csvEvent,
                  showAppPackage,
                },
                clickExportCsvButtonCallback,
              ),
            );
          }}
        >
          Export
        </Button>
        <HiddenCSVLink data={csvData} filename={`${csvEvent}.csv`} ref={csvLink} target="_blank" />
      </ModalFooter>
    </Modal>
  );
};
